import { z } from "zod"

import {
  branchIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
  tenantIdSchema,
} from "./identifiers.js"

const accountNameSchema = z.string().trim().min(1).max(140)

export const financeReferenceDataRequestV1Schema = z.object({
  requestVersion: z.literal(1),
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
}).strict()

export const financeReferenceDataV1Schema = z.object({
  referenceVersion: z.literal(1),
  tenantId: tenantIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  authoritativeSource: z.literal("frappe_erpnext"),
  company: z.string().trim().min(1).max(140),
  currency: z.string().regex(/^[A-Z]{3}$/),
  costCenter: z.string().trim().min(1).max(140),
  accountingDimension: z.string().trim().min(1).max(140),
  configuration: z.object({
    configurationId: entityIdSchema,
    currentRevisionId: entityIdSchema,
    version: z.number().int().positive(),
    fiscalYear: z.string().trim().min(1).max(140),
    fiscalYearStart: z.iso.date(),
    fiscalYearEnd: z.iso.date(),
    completionGraceMinutes: z.number().int().min(0).max(1_440),
    accounts: z.object({
      receivableAccount: accountNameSchema,
      payableAccount: accountNameSchema,
      incomeAccount: accountNameSchema,
      expenseAccount: accountNameSchema,
      roundOffAccount: accountNameSchema,
    }).strict(),
  }).strict().nullable(),
  accounts: z.array(z.object({
    name: accountNameSchema,
    rootType: z.enum(["Asset", "Liability", "Equity", "Income", "Expense"]),
    accountType: z.string().trim().max(80),
  }).strict()).max(200),
  customers: z.array(z.string().trim().min(1).max(140)).max(100),
  suppliers: z.array(z.string().trim().min(1).max(140)).max(100),
  items: z.array(z.string().trim().min(1).max(140)).max(100),
  lateEvents: z.array(z.object({
    evidenceId: entityIdSchema,
    sourceType: z.string().trim().min(1).max(80),
    sourceVersion: z.number().int().positive(),
    originalPostingDate: z.iso.date(),
    currency: z.string().regex(/^[A-Z]{3}$/),
    amountMinor: z.number().int().positive().safe(),
  }).strict()).max(100),
  financialPostings: z.array(z.object({
    postingId: entityIdSchema,
    version: z.number().int().positive(),
    state: z.enum(["posted", "reversed"]),
    currency: z.string().regex(/^[A-Z]{3}$/),
    totalMinor: z.number().int().positive().safe(),
    originalEventAt: z.iso.datetime({ offset: true }),
    journalEntry: z.string().trim().min(1).max(140),
  }).strict()).max(100),
  taxReviews: z.array(z.object({
    reviewId: entityIdSchema,
    version: z.number().int().positive(),
    reasonCode: z.enum(["missing_rule", "ambiguous_rule", "manual_override"]),
  }).strict()).max(100),
  taxRuleVersions: z.array(z.object({
    ruleVersionId: entityIdSchema,
    label: z.string().trim().min(1).max(140),
  }).strict()).max(100),
}).strict()

export type FinanceReferenceDataRequestV1 = z.infer<
  typeof financeReferenceDataRequestV1Schema
>
export type FinanceReferenceDataV1 = z.infer<typeof financeReferenceDataV1Schema>
